/** Add Property — v1 `openAddProperty` / `submitAddProperty`, field for field:
 * Basic (address + Census autocomplete) / Property Details / Financial / Lead /
 * Flags. The address check hits the same read-only backend v1 uses
 * (/api/arv/normalize + /api/inbox/market-check) and is safe in Phase A.
 *
 * CREATE is Phase-B-gated (ADD_PROPERTY_LIVE): a manual lead is a real row in
 * the shared DB v1 runs on, same rule as Send/Upload. The form, autocomplete
 * and Zillow check all work now; the Create button flips on in the sit-down. */
import { useRef, useState } from "react";
import { STATUS_OPTIONS } from "../edits/api";
import {
  ADD_MSG, ADD_PROPERTY_LIVE, PROPERTY_TYPES, V1_SOURCES,
  emptyAddPropertyForm, fullAddress, normalizeAddress, zillowVerify,
  type AddPropertyForm,
} from "./api";

export function AddPropertyModal({
  token,
  onCreate,
  onClose,
  onWake,
}: {
  token: string;
  onCreate: (payload: { form: AddPropertyForm; zillowUrl: string | null }) => void;
  onClose: () => void;
  onWake?: () => void;
}) {
  const [f, setF] = useState<AddPropertyForm>(emptyAddPropertyForm);
  const [sugs, setSugs] = useState<string[]>([]);
  const [checking, setChecking] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [zillowUrl, setZillowUrl] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const set = <K extends keyof AddPropertyForm>(k: K, v: AddPropertyForm[K]) => setF((p) => ({ ...p, [k]: v }));

  const onAddr = (v: string) => {
    set("address", v);
    setNote(null);
    setZillowUrl(null);
    if (timer.current) clearTimeout(timer.current);
    if (v.trim().length < 4) { setSugs([]); return; }
    timer.current = setTimeout(() => {
      void normalizeAddress(token, v.trim(), onWake ? { onWake } : {}).then((ms) => setSugs(ms.map((m) => m.address).slice(0, 6)));
    }, 350);
  };

  const pick = (addr: string) => {
    const parts = addr.split(",").map((x) => x.trim());
    setF((p) => ({ ...p, address: addr, city: parts[1] ?? p.city, state: (parts[2] ?? p.state).toUpperCase(), zip: parts[3] ?? p.zip }));
    setSugs([]);
  };

  const check = () => {
    const addr = fullAddress(f);
    if (!addr) { setNote(ADD_MSG.addressRequired); return; }
    setChecking(true);
    setNote(ADD_MSG.checking);
    void zillowVerify(token, addr, onWake ? { onWake } : {}).then((z) => {
      setChecking(false);
      if (z.found) {
        const url = typeof z.data?.["property_url"] === "string" ? (z.data["property_url"] as string) : null;
        setZillowUrl(url);
        // pull county if the check resolved one and the field is empty
        const cty = typeof z.data?.["county"] === "string" ? (z.data["county"] as string) : "";
        if (cty && !f.county.trim()) set("county", cty);
        setNote(ADD_MSG.verified);
      } else {
        setNote(z.noApi ? ADD_MSG.unavailable : ADD_MSG.notFound);
      }
    });
  };

  const create = () => {
    if (!fullAddress(f)) { setNote(ADD_MSG.addressRequired); return; }
    onCreate({ form: f, zillowUrl });
  };

  const fld = (label: string, node: React.ReactNode) => (
    <label className="cmfld"><span className="lbl">{label}</span>{node}</label>
  );
  const numInput = (k: keyof AddPropertyForm, ph: string) => (
    <input inputMode="numeric" value={f[k] as string} placeholder={ph} onChange={(e) => set(k, e.target.value as AddPropertyForm[typeof k])} />
  );
  const toggle = (k: keyof AddPropertyForm, label: string) => (
    <label className="apflag">
      <input type="checkbox" checked={f[k] as boolean} onChange={(e) => set(k, e.target.checked as AddPropertyForm[typeof k])} />
      {label}
    </label>
  );

  return (
    <div className="calmodalwrap" onClick={onClose}>
      <div className="calmodal wide cmodal" onClick={(e) => e.stopPropagation()}>
        <header className="calmodalhead">
          <strong>{ADD_MSG.title}</strong>
          <button className="close" onClick={onClose}>×</button>
        </header>
        <div className="calmodalbody cmbody">
          <div className="apsec">Basic information</div>
          <div className="apaddr">
            {fld("Address *", (
              <input
                value={f.address}
                placeholder="123 Main Street"
                autoComplete="off"
                onChange={(e) => onAddr(e.target.value)}
              />
            ))}
            {sugs.length ? (
              <div className="apsug">
                {sugs.map((s) => (
                  <button key={s} type="button" className="apsugrow" onMouseDown={(e) => { e.preventDefault(); pick(s); }}>{s}</button>
                ))}
              </div>
            ) : null}
          </div>
          <div className="cmrow">
            {fld("City", <input value={f.city} placeholder="Orlando" onChange={(e) => set("city", e.target.value)} />)}
            {fld("County", <input value={f.county} placeholder="Orange" onChange={(e) => set("county", e.target.value)} />)}
          </div>
          <div className="cmrow">
            {fld("State", <input value={f.state} placeholder="FL" onChange={(e) => set("state", e.target.value)} />)}
            {fld("ZIP", <input value={f.zip} placeholder="32801" onChange={(e) => set("zip", e.target.value)} />)}
          </div>
          <div className="apcheckrow">
            <button className="btn slim" disabled={checking} onClick={check}>{checking ? "Checking…" : "Check on Zillow"}</button>
            {note ? <span className={"apnote" + (note === ADD_MSG.verified ? " ok" : "")}>{note}</span> : null}
          </div>

          <div className="apsec">Property details</div>
          <div className="cmrow three">
            {fld("Type", (
              <select value={f.ptype} onChange={(e) => set("ptype", e.target.value)}>
                <option value="">Select type</option>
                {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            ))}
            {fld("Bedrooms", numInput("beds", ""))}
            {fld("Bathrooms", numInput("baths", ""))}
          </div>
          <div className="cmrow three">
            {fld("SqFt", numInput("sqft", ""))}
            {fld("Year Built", numInput("year", ""))}
            {fld("Lot Size", <input value={f.lot} placeholder="0.25 acres" onChange={(e) => set("lot", e.target.value)} />)}
          </div>

          <div className="apsec">Financial</div>
          <div className="cmrow three">
            {fld("Asking Price", numInput("price", "$"))}
            {fld("Estimated ARV", numInput("arv", "$"))}
            {fld("Estimated Rehab", numInput("rehab", "$"))}
          </div>

          <div className="apsec">Lead information</div>
          <div className="cmrow">
            {fld("Lead Status", (
              <select value={f.status} onChange={(e) => set("status", e.target.value)}>
                <option value="">Select status</option>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            ))}
            {fld("Lead Source", (
              <select value={f.source} onChange={(e) => set("source", e.target.value)}>
                <option value="">Select source</option>
                {V1_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            ))}
          </div>
          {fld("Notes", <textarea rows={3} value={f.notes} placeholder="Additional notes…" onChange={(e) => set("notes", e.target.value)} />)}

          <div className="apsec">Property flags</div>
          <div className="apflags">
            {toggle("pool", "Pool")}
            {toggle("garage", "Garage")}
            {toggle("sinkhole", "Sinkhole")}
            {toggle("foundation", "Foundation issues")}
            {toggle("distressed", "Distressed seller")}
            {toggle("onMarket", "On market")}
            {toggle("underContract", "Under contract")}
          </div>
        </div>
        <footer className="calmodalfoot">
          <button className="vtab" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!ADD_PROPERTY_LIVE} title={ADD_PROPERTY_LIVE ? "" : ADD_MSG.createPhaseB} onClick={create}>
            {ADD_PROPERTY_LIVE ? "Create Property" : "Create Property — Phase B"}
          </button>
        </footer>
      </div>
    </div>
  );
}
